import { useState } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { Input, InputProps } from '../../components';
import { Search } from '../../icons';
import { cn } from '../../utils';
import { FormField } from '../BaseFormComponents';
import { BaseFormFieldWrapper } from '../BaseFormFieldWrapper';
import { FormComponentBaseProps } from '../types/form-base-props';
import { FormTag } from './FormTag';
import { useTagErrors } from './FormTagField.utils';

export type FormTagFieldProps = FormComponentBaseProps & InputProps;

export const FormTagField = ({
  description,
  descriptionProps,
  label,
  labelProps,
  hideError = true,
  defaultValue,
  name,
  disabled,
  required,
  placeholder,
  ...props
}: FormTagFieldProps) => {
  const [inputValue, setInputValue] = useState('');
  const { control } = useFormContext();
  const {
    fieldState: { error },
  } = useController({ name, control });

  const tagErrors = useTagErrors(error);

  return (
    <FormField
      shouldUnregister={false}
      disabled={disabled}
      name={name}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => {
        const onValueAdd = () => {
          if (!inputValue) return;
          field.onChange([...field.value, inputValue]);
          setInputValue('');
        };

        return (
          <BaseFormFieldWrapper
            description={description}
            descriptionProps={descriptionProps}
            label={label}
            labelProps={labelProps}
            hideError={hideError}
          >
            <div
              className={cn(
                'border-border flex flex-wrap gap-2 rounded-sm border px-[14px] py-[5px]',
                field.value.length === 0
                  ? 'flex-row items-center'
                  : 'flex-col items-start justify-center sm:flex-row sm:items-center'
              )}
            >
              {Array.isArray(field.value) ? (
                field.value.length === 0 ? (
                  <Search className="size-4" />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {field.value.map((value, index) => {
                      const error = tagErrors.find(err => err.tag === value);

                      return (
                        <FormTag
                          key={`tag-${value}-${index}`}
                          value={value}
                          variant={error?.variant}
                          onRemove={() =>
                            field.onChange(
                              field.value.filter(
                                (_: unknown, valueIndex: number) =>
                                  index !== valueIndex
                              )
                            )
                          }
                        />
                      );
                    })}
                  </div>
                )
              ) : null}
              <div className="flex-[1_0]">
                <Input
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  className="border-none p-1"
                  placeholder={placeholder}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onValueAdd();
                    }
                  }}
                  onBlur={onValueAdd}
                />
              </div>
            </div>
          </BaseFormFieldWrapper>
        );
      }}
    />
  );
};
